'use client'
import { Card } from '../ui/card'
import type { Testimonial } from '@/helper/serverFetchers/testimonials'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { useRef } from 'react'
import CarouselNavigation from '@/components/common/CarouselNavigation'
import { useMediaQuery } from '@/hooks/use-media-query'

const QuoteIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 32 32"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.8 5.33334L10.1333 10.6667H14.9333V26.6667H1.06665V10.6667L6.39998 0H12.8V5.33334ZM30.9333 5.33334L28.2666 10.6667H33.0666V26.6667H19.2V10.6667L24.5333 0H30.9333V5.33334Z"
      fill="currentColor"
      transform="scale(0.75) translate(2, 2)"
    />
  </svg>
)

const TestimonialSlider = ({ testimonialsdata }: any) => {
  const { value: isDesktop } = useMediaQuery('(min-width: 640px)')

  // The name is the React key, so an entry without one is dropped rather than
  // rendered. Guards the array shape too - .map on a non-array throws.
  const testimonials: Testimonial[] = (
    Array.isArray(testimonialsdata) ? testimonialsdata : []
  ).filter((item: any) => item?.name)

  const swiperRef = useRef<SwiperType | null>(null)

  const goToNextSlide = () => {
    if (swiperRef.current) {
      swiperRef.current.slideNext()
    }
  }

  const goToPrevSlide = () => {
    if (swiperRef.current) {
      swiperRef.current.slidePrev()
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 sm:mb-8">
        <div>
          <h3 className="text-heading-md md:text-heading-lg text-gray-900 mb-0 sm:mb-2">
            What Our Customers Say
          </h3>
        </div>
      </div>

      <div className="relative">
        <CarouselNavigation
          goToPrevSlide={goToPrevSlide}
          goToNextSlide={goToNextSlide}
        />
        <Swiper
          loop
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          navigation
          pagination={!isDesktop}
          slidesPerView={isDesktop ? 'auto' : 1.1}
          spaceBetween={isDesktop ? 24 : 16}
          className="sm:w_fit_slider navigation_hidden testimonials_slider"
          modules={[Autoplay, Navigation, Pagination]}
          autoplay={{ delay: 4000, disableOnInteraction: false }}
        >
          {testimonials.map((item: Testimonial) => {
            return (
              <SwiperSlide
                key={item.name}
                className="h_auto sm:max-w-[420px] w-full sm:w-full"
                style={{ width: '100%' }}
              >
                <Card
                  className="border border-gray-200 bg-gray-50 rounded-lg p-5 sm:p-6 h-full max-w-[420px] w-full transition-all duration-300 flex flex-col justify-between"
                  role="article"
                >
                  <div>
                    <p className="text-body-sm sm:text-body-md text-gray-800 leading-relaxed line-clamp-4">
                      <span className="text-primary-400 font-serif text-body-lg mr-0.5">
                        &ldquo;
                      </span>
                      {item.feedback}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <p className="text-label-sm font-semibold text-gray-900 truncate">
                      {item.name}
                    </p>
                    {item.location && (
                      <p className="text-caption text-gray-700 truncate">
                        {item.location}
                      </p>
                    )}
                  </div>
                </Card>
              </SwiperSlide>
            )
          })}
        </Swiper>
      </div>
    </div>
  )
}

export default TestimonialSlider
